import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { RostosDoReconhecimento } from 'components/person-avatar/rostos-do-reconhecimento';
import { ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO } from 'pages/Rede/rotas-do-feed';
import { getPrefixRecognitions, RostoDoAutor, RostoDoDestinatario } from 'services/home-feed.service';

import { toRecentRecognition } from './recent-recognitions.format';
import { ArrowLink } from './section-heading';

/** Sem o rosto de quem enviou (`RostoDoAutor`) ou de quem recebeu (`RostoDoDestinatario`), as iniciais. */
export interface RecentRecognition extends RostoDoAutor, RostoDoDestinatario {
  id: string;
  author: string;
  recipient: string;
  /** Tempo relativo já formatado ("há 4 min", "ontem"). */
  when: string;
  message: string;
  behavior: string;
  /** "#UAU", "#MANDOUBEM", "#VALEU"; ausente quando a moeda e os pontos não dizem (HOME-19). */
  level?: string;
  /** Pontos do reconhecimento ("+300 pts" no Feed Joga Junto). */
  points?: number;
}

/** `idle`: ainda sem prefixo para ler. `failed`: a leitura falhou (a lista fica vazia). */
export type RecognitionsStatus = 'idle' | 'loading' | 'ready' | 'failed';

interface SettledRead {
  prefixo: number;
  failed: boolean;
  items: RecentRecognition[];
}

const NO_ITEMS: RecentRecognition[] = [];

function readStatus(prefixo: number | undefined, current: SettledRead | undefined): RecognitionsStatus {
  if (!prefixo) return 'idle';
  if (!current) return 'loading';

  return current.failed ? 'failed' : 'ready';
}

/**
 * Últimos reconhecimentos do prefixo, lidos do cache que o scheduler recalcula de 2 em 2 horas
 * (FEED-11). Falha ou cache vazio viram lista vazia, e o painel diz qual dos dois foi (FEED-13).
 */
export function usePrefixRecognitions(prefixo?: number) {
  // Última leitura concluída, com o prefixo dela. O carregando sai da comparação com o prefixo
  // pedido, e não de um estado ligado no efeito, para não piscar o vazio antes da leitura; e os
  // itens só valem para o prefixo que os trouxe, para a troca de prefixo não mostrar os do anterior.
  const [settled, setSettled] = useState<SettledRead>();

  useEffect(() => {
    if (!prefixo) return;

    let cancelled = false;

    getPrefixRecognitions(prefixo)
      .then(cached => {
        if (cancelled) return;
        setSettled({ prefixo, failed: false, items: cached.map(item => toRecentRecognition(item)) });
      })
      .catch(() => {
        if (cancelled) return;
        setSettled({ prefixo, failed: true, items: NO_ITEMS });
      });

    return () => {
      cancelled = true;
    };
  }, [prefixo]);

  const current = settled?.prefixo === prefixo ? settled : undefined;

  return { items: current?.items ?? NO_ITEMS, status: readStatus(prefixo, current) };
}

// D3: 18px não tem degrau no Tailwind (também nos cartões abaixo).
const NOTICE_CLASSES = 'rounded-jj-lg bg-brand-white px-[18px] py-5 text-jj-body-sm leading-normal text-content';

/** Acima disso a mensagem ganha "ver mais" (AJ3-21, `longo` do Home.dc.html). */
export const MENSAGEM_LONGA = 80;

/**
 * Inclinação, deslocamento e atraso de entrada de cada linha, e o levantar no hover, como no Home.dc.html (AJ3-23).
 * Com movimento reduzido, sem entrada (`.jj-rec-in` no `App.scss`) e sem levantar.
 */
const ROW_MOTION = [
  '[--rot:-0.8deg] [--dx:-4px] [animation-delay:0ms]',
  '[--rot:0.7deg] [--dx:8px] [animation-delay:90ms]',
  '[--rot:-0.5deg] [--dx:-2px] [animation-delay:180ms]',
];

const ROW_CLASSES =
  'relative flex items-start gap-3.5 rounded-lg bg-brand-white px-[18px] py-3.5 text-content shadow-[0_4px_12px_rgba(0,45,75,0.08)] ' +
  '[transform:translateX(var(--dx))_rotate(var(--rot))] jj-rec-in transition-[transform,box-shadow] duration-200 ease-[cubic-bezier(.4,0,.2,1)] ' +
  'hover:z-[2] hover:[transform:translateX(var(--dx))_translateY(-3px)_rotate(calc(var(--rot)/2))] hover:shadow-[0_14px_28px_rgba(0,45,75,0.16)] ' +
  'motion-reduce:transition-none motion-reduce:hover:[transform:translateX(var(--dx))_rotate(var(--rot))]';

/** Cartões de espera, no formato dos cartões da lista, enquanto a leitura não chega. */
function RecognitionsSkeleton() {
  return (
    <div role="status" className="flex flex-col gap-2.5">
      <span className="screen-reader-text">Carregando os últimos reconhecimentos do prefixo...</span>

      {[0, 1].map(card => (
        <div
          key={card}
          aria-hidden="true"
          className="flex items-start gap-3.5 rounded-jj-lg bg-brand-white px-[18px] py-3.5 motion-safe:animate-pulse"
        >
          {/* D3: 34px não tem degrau no Tailwind. */}
          <span className="mt-1.5 h-[34px] w-[34px] shrink-0 rounded-full bg-surface-200" />
          <span className="flex flex-1 flex-col gap-2 py-1">
            <span className="h-3 w-2/5 rounded-full bg-surface-200" />
            <span className="h-3 w-full rounded-full bg-surface-200" />
            <span className="h-3 w-1/4 rounded-full bg-surface-200" />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Uma linha de reconhecimento (AJ3-19 a AJ3-23): os dois rostos, quem reconheceu quem, o tempo, "{tipo} / {comportamento}"
 * e a mensagem numa linha, com "ver mais" quando passa de 80 caracteres. O nome é o link para os reconhecimentos do
 * prefixo e cobre a linha inteira (`after:inset-0`); o "ver mais" fica por cima dele e não navega.
 */
function RecognitionRow({ item, index }: { item: RecentRecognition; index: number }) {
  const [open, setOpen] = useState(false);
  const messageId = useId();
  const long = item.message.length > MENSAGEM_LONGA;

  return (
    <li className={`${ROW_CLASSES} ${ROW_MOTION[index % ROW_MOTION.length]}`}>
      <span className="mt-0.5">
        <RostosDoReconhecimento
          item={item}
          tamanho={34}
          iniciaisDoAutor="bg-surface-200 font-display text-[11px] font-bold text-content"
          iniciaisDoDestinatario="bg-brand-purple-light font-display text-[11px] font-bold text-brand-blue-dark"
        />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-[7px]">
          <Link
            to={ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO}
            className="font-display text-[13px] font-bold text-content no-underline after:absolute after:inset-0 after:rounded-lg after:content-[''] hover:text-content hover:no-underline focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-brand-blue-dark"
          >
            {item.author} reconheceu {item.recipient}
          </Link>
          <span className="text-[11px] text-content-soft">{item.when}</span>
          <span className="ml-auto whitespace-nowrap text-[10.5px] font-bold uppercase tracking-[.03em] text-brand-blue-dark">
            {[item.level, item.behavior].filter(Boolean).join(' / ')}
          </span>
        </span>

        <span className="flex min-w-0 items-baseline gap-2">
          <span
            id={messageId}
            className={`min-w-0 flex-1 overflow-hidden text-ellipsis text-[12.5px] leading-[1.5] text-content ${open ? 'whitespace-normal' : 'whitespace-nowrap'}`}
          >
            “{item.message}”
          </span>
          {long && (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={messageId}
              onClick={() => setOpen(value => !value)}
              className="relative z-[1] shrink-0 cursor-pointer self-end whitespace-nowrap border-0 bg-transparent p-0 font-text text-[11px] font-bold text-brand-blue-dark underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark"
            >
              {open ? 'ver menos' : 'ver mais'}
            </button>
          )}
        </span>
      </span>
    </li>
  );
}

interface RecentRecognitionsProps {
  items: RecentRecognition[];
  prefixo?: number;
  /** Padrão `ready`: a lista como veio. */
  status?: RecognitionsStatus;
}

/**
 * "Últimos reconhecimentos" do bloco rosa de "Reconheça seus colegas": a lista e, sem ela, a
 * espera da leitura, o aviso de falha ou o de prefixo ainda sem reconhecimentos (FEED-13).
 */
export function RecentRecognitions({ items, prefixo, status = 'ready' }: RecentRecognitionsProps) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3.5">
        <h3 className="font-display text-jj-body font-bold leading-tight text-brand-blue-dark">
          Últimos reconhecimentos
          {prefixo && <span className="text-content"> • seu prefixo {prefixo}</span>}
        </h3>

        <ArrowLink to={ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO}>Ver todos no Feed Joga Junto</ArrowLink>
      </div>

      {status === 'loading' && <RecognitionsSkeleton />}

      {status === 'failed' && (
        <p className={NOTICE_CLASSES}>Não foi possível carregar os reconhecimentos do prefixo agora. Tente de novo mais tarde.</p>
      )}

      {status === 'ready' && items.length === 0 && (
        <p className={NOTICE_CLASSES}>
          <b className="font-display text-jj-body-sm font-bold">Ainda não há reconhecimentos recentes no seu prefixo.</b>
          <br />
          Quando alguém do seu prefixo reconhecer um colega, o reconhecimento aparece aqui.
        </p>
      )}

      {status === 'ready' && items.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {items.map((item, index) => (
            <RecognitionRow key={item.id} item={item} index={index} />
          ))}
        </ul>
      )}
    </div>
  );
}
